<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { useCaseStore } from "@/stores";

import type { MigrationPlan, Operation } from "@/models/RecordMigration";

const selectedPlan = defineModel<MigrationPlan>("selectedPlan");

const caseStore = useCaseStore();

const plans = computed(() => caseStore.migrationPlans);

const selectedPlanId = ref<string>();

function describeDirection(plan: (typeof plans.value)[number]): string {
  const caseName = caseStore.selectedCaseName?.toLowerCase();
  const isSource = plan.sourceCaseReference.toLowerCase() === caseName;
  const isDestination = plan.destinationCaseReference.toLowerCase() === caseName;

  if (isSource && isDestination) return "source and destination";
  if (isSource) return `source, to "${plan.destinationCaseReference}"`;
  if (isDestination) return `destination, from "${plan.sourceCaseReference}"`;
  return "";
}

function describeCase(caseReference: string): string {
  return caseReference.toLowerCase() === caseStore.selectedCaseName?.toLowerCase() ? "This case" : caseReference;
}

function describeOperation(operation: Operation): string {
  return operation.answers
    .map((answer) => {
      const decision = operation.requiredDecisions.find((d) => d.id === answer.decisionId);
      const optionLabel = decision?.allowedOptions.find((o) => o.value === answer.option)?.label ?? answer.option;
      return `${decision?.prompt ?? answer.decisionId}: ${optionLabel}`;
    })
    .join("; ");
}

watch(selectedPlanId, (id) => {
  selectedPlan.value = plans.value.find((plan) => plan.id === id);
});
</script>
<template>
  <div v-if="plans.length > 0" class="tw-flex tw-shrink-0 tw-flex-col tw-gap-2">
    <div class="tw-flex tw-items-center tw-gap-2">
      <label for="migration-existing-plan" class="tw-text-xs">Existing Migrations for this Case</label>
      <select id="migration-existing-plan" v-model="selectedPlanId" class="tw-form-select tw-text-sm">
        <option :value="undefined">Select a migration to view details...</option>
        <option v-for="plan in plans" :key="plan.id" :value="plan.id">
          {{ plan.action === "copy" ? "Copy" : "Move" }} ({{ describeDirection(plan) }})
        </option>
      </select>
      <button type="button" class="tw-btn-md-gray" :disabled="!selectedPlan" @click="selectedPlanId = undefined">
        Close
      </button>
    </div>
    <div
      v-if="selectedPlan"
      role="region"
      aria-label="Selected migration details"
      class="tw-flex tw-flex-col tw-gap-3 tw-rounded-lg tw-border tw-border-gray-300 tw-bg-gray-100 tw-p-3 tw-text-sm"
    >
      <dl class="tw-grid tw-grid-cols-[max-content_1fr] tw-items-baseline tw-gap-x-6 tw-gap-y-2">
        <dt class="tw-text-xs tw-text-gray-800">Plan ID</dt>
        <dd>{{ selectedPlan.id }}</dd>
        <dt class="tw-text-xs tw-text-gray-800">Operation</dt>
        <dd>{{ selectedPlan.action === "copy" ? "Copy" : "Move" }}</dd>
        <dt class="tw-text-xs tw-text-gray-800">Source Case</dt>
        <dd>{{ describeCase(selectedPlan.sourceCaseReference) }}</dd>
        <dt class="tw-text-xs tw-text-gray-800">Target Case</dt>
        <dd>{{ describeCase(selectedPlan.destinationCaseReference) }}</dd>
      </dl>
      <div class="tw-flex tw-flex-col tw-gap-2">
        <p class="tw-text-xs tw-text-gray-800">Files ({{ selectedPlan.operations.length }})</p>
        <p class="tw-text-xs tw-italic tw-text-gray-800">
          File details reflect the case content at the time this plan was generated and may not match any changes made
          since.
        </p>
        <table class="tw-w-full tw-rounded tw-border tw-border-gray-300 tw-bg-white tw-text-left tw-text-xs">
          <thead>
            <tr class="tw-border-b tw-border-gray-300 tw-text-gray-800">
              <th class="tw-p-2 tw-font-medium">Name</th>
              <th class="tw-p-2 tw-font-medium">File Reference</th>
              <th class="tw-p-2 tw-font-medium">Classification</th>
              <th class="tw-p-2 tw-font-medium">Issue Date</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="operation in selectedPlan.operations" :key="operation.fileReference">
              <tr class="tw-border-b tw-border-gray-300">
                <td class="tw-p-2">{{ operation.displayName ?? "-" }}</td>
                <td class="tw-p-2 tw-font-mono">{{ operation.fileReference }}</td>
                <td class="tw-p-2">{{ operation.classification ?? "-" }}</td>
                <td class="tw-p-2">{{ operation.edxIssueDate ?? "-" }}</td>
              </tr>
              <tr class="tw-border-b tw-border-gray-300 tw-bg-gray-100">
                <td colspan="4" class="tw-p-2 tw-pl-6 tw-text-gray-800">
                  {{ operation.answers.length > 0 ? describeOperation(operation) : "No decisions required" }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
